import { Metric } from 'modules/messagingInsights/types';
import { localizeCompactNumber } from 'utils/l10n/number';
import { localizePercent } from 'utils/l10n/percent';
import { TOTAL_SENT_METRIC_NAME } from '../CampaignsHealthMetrics.constants';
import { TableData, TableDataByMedium } from './MetricsTable.types';
import { CAMPAIGNS_DISPLAY_NAMES_TO_MEDIUM_MAP } from './MetricsTable.constants';
import { HealthAggsByMetricAndMedium } from 'modules/missionControl/types/health';

/**
 * Checks if a medium has any send data
 * @returns True if the medium has no send data, false otherwise
 */
const checkIfMediumHasNoSendData = (metricsData: Record<string, number | null>) => {
  return Object.values(metricsData).every((value) => !value);
};

/**
 * @returns The table data rows that have send data
 */
const getFilteredTableDataRows = (allTableDataByMedium: TableDataByMedium): TableData => {
  return Object.entries(allTableDataByMedium).reduce(
    (acc: TableData, [mediumName, metricsData]) => {
      if (checkIfMediumHasNoSendData(metricsData)) return acc;
      return [
        ...acc,
        {
          mediumName,
          metricsData,
        },
      ];
    },
    [],
  );
};

/**
 * @returns additional high level metric data for a given metric name
 */
const getHighLevelMetric = (highLevelMetrics: Metric[], metricName: string) =>
  highLevelMetrics.find((metric: Metric) => metric.metricName === metricName);

/**
 * Parses metrics data returned from the backend
 * @returns The table data for the cross medium metrics sorted by medium
 */
const getTableDataByMedium = (
  perMediumMetrics: HealthAggsByMetricAndMedium,
  highLevelMetrics: Metric[],
): TableDataByMedium => {
  return Object.entries(perMediumMetrics).reduce(
    (acc: TableDataByMedium, [metricName, metricResults]) => {
      // only include metrics that are percentage rates, not total values
      // 'Total Sent' is a special case that we want to include, per designs
      if (metricName !== TOTAL_SENT_METRIC_NAME && metricName.includes('Total')) {
        return acc;
      }
      const highLevelMetricData = getHighLevelMetric(highLevelMetrics, metricName);
      const { mediums: supportedMediums = [] } = highLevelMetricData || {};
      const mediums = metricResults.reduce((acc, curr) => ({ ...acc, ...curr }), {});

      Object.entries(mediums).forEach(([mediumName, mediumData]) => {
        const medium = CAMPAIGNS_DISPLAY_NAMES_TO_MEDIUM_MAP[mediumName];
        const isSupportedMedium = supportedMediums?.includes(medium);

        const { value } = mediumData;

        if (!acc[mediumName]) {
          acc[mediumName] = {};
        }
        // sort each metric into its respective medium
        acc[mediumName][metricName] = isSupportedMedium ? value : null;
      });

      return acc;
    },
    {},
  );
};

/**
 * Parses metrics data returned from the backend by
 *  1. Only including the relevant metrics (i.e. only percentage rates metrics)
 *  2. Sorting the metrics by medium
 *  3. Removing the rows that have no send data
 * @param perMediumMetrics - The per medium metrics returned from backend
 * @param highLevelMetrics - The high level metrics data returned from backend
 * @returns The table data for the cross medium metrics in rows for rendering
 */
export const getCrossMediumTableData = (
  perMediumMetrics: HealthAggsByMetricAndMedium,
  highLevelMetrics: Metric[],
): TableData => {
  const tableDataByMedium = getTableDataByMedium(perMediumMetrics, highLevelMetrics);
  return getFilteredTableDataRows(tableDataByMedium);
};

/**
 * Returns the text to display for a given metric value
 * @param metricName - The name of the metric
 * @param metricValue - The value of the metric
 * @returns The text to display for the metric
 */
export const getMetricValueText = (metricName: string, metricValue: number | null) => {
  if (metricValue === null) {
    return '--';
  } else if (metricName !== TOTAL_SENT_METRIC_NAME) {
    return localizePercent(Number(metricValue || 0));
  }

  return localizeCompactNumber(metricValue);
};
